import type { Dayjs } from 'dayjs'

/**
 * One row of the facility_distribution percentage-targets editor. `level`
 * picks the entity kind — 'venue' renders as "Location", 'facility' as
 * "Surface" (calendar vocabulary) — and `entity_uid` + `percent` complete
 * the target.
 */
export interface FacilityPercentageTargetRow {
  level: 'facility' | 'venue'
  entity_uid?: string
  percent?: number
}

/** API target entry shape ({facility_uid|venue_uid, percent}). */
export interface FacilityPercentageTarget {
  facility_uid?: string
  venue_uid?: string
  percent?: number
}

/** Editor rows → API `parameters.targets` entries. */
export const facilityPercentageRowsToTargets = (
  rows: FacilityPercentageTargetRow[] | undefined
): FacilityPercentageTarget[] =>
  (rows ?? []).map((row) =>
    row.level === 'venue'
      ? { venue_uid: row.entity_uid, percent: row.percent }
      : { facility_uid: row.entity_uid, percent: row.percent }
  )

/** API `parameters.targets` entries → editor rows (edit-mode prefill). */
export const facilityPercentageTargetsToRows = (
  targets: FacilityPercentageTarget[] | undefined
): FacilityPercentageTargetRow[] =>
  (targets ?? []).map((t) =>
    t.venue_uid
      ? { level: 'venue', entity_uid: t.venue_uid, percent: t.percent }
      : { level: 'facility', entity_uid: t.facility_uid, percent: t.percent }
  )

/**
 * Row-level validation shared by the modal's Form.List validator and the
 * imperative `validateConstraintValues` path. Mirrors the api2 /
 * scheduler-python rules: non-empty; every row complete (entity + percent
 * 0-100); no duplicate entity; per-level percents sum to <= 100.
 * Returns the first error message, or null when valid.
 */
export const validateFacilityPercentageRows = (
  rows: FacilityPercentageTargetRow[] | undefined
): string | null => {
  if (!rows || rows.length === 0) {
    return 'At least one percentage target is required'
  }
  const seen = new Set<string>()
  const levelSums: Record<'facility' | 'venue', number> = {
    facility: 0,
    venue: 0,
  }
  for (const row of rows) {
    if (!row || !row.entity_uid) {
      return 'Every target needs a location or surface'
    }
    if (row.percent == null) {
      return 'Every target needs a percentage'
    }
    // Whole numbers only — api2 and the scheduler reject non-integers, so
    // catch them client-side instead of surfacing a backend 422.
    if (
      !Number.isInteger(row.percent) ||
      row.percent < 0 ||
      row.percent > 100
    ) {
      return 'Percentages must be whole numbers between 0 and 100'
    }
    const key = `${row.level}:${row.entity_uid}`
    if (seen.has(key)) {
      return 'Each location or surface can only be listed once'
    }
    seen.add(key)
    levelSums[row.level] += row.percent
  }
  if (levelSums.facility > 100) {
    return `Surface percentages add up to ${levelSums.facility}% — they cannot exceed 100%`
  }
  if (levelSums.venue > 100) {
    return `Location percentages add up to ${levelSums.venue}% — they cannot exceed 100%`
  }
  return null
}

/**
 * Build API `parameters` object from form values for a given constraint type.
 */
export const buildConstraintParameters = (
  typeCode: string,
  values: Record<string, unknown>
): Record<string, unknown> => {
  switch (typeCode) {
    case 'venue_blackout': {
      const dates = values.dates as Dayjs[] | undefined
      const parameters: Record<string, unknown> = {
        dates: (() => {
          if (!dates || dates.length < 2) {
            return dates?.map((d: Dayjs) => d.format('YYYY-MM-DD')) || []
          }
          const start = dates[0]
          const end = dates[1]
          const result: string[] = []
          let current = start.startOf('day')
          const endDay = end.startOf('day')
          while (current.isBefore(endDay) || current.isSame(endDay, 'day')) {
            result.push(current.format('YYYY-MM-DD'))
            current = current.add(1, 'day')
          }
          return result
        })(),
      }
      const venueUids = values.venue_uids as string[] | undefined
      if (venueUids && venueUids.length > 0) {
        parameters.venue_uids = venueUids
      }
      const facilityUids = values.facility_uids as string[] | undefined
      if (facilityUids && facilityUids.length > 0) {
        parameters.facility_uids = facilityUids
      }
      return parameters
    }

    case 'venue_capacity':
      return {
        facility_uids: values.facility_uids,
        max_concurrent_games: values.max_concurrent_games,
      }

    case 'time_window': {
      const params: Record<string, unknown> = {
        start_time: (values.start_time as Dayjs)?.format('HH:mm'),
        end_time: (values.end_time as Dayjs)?.format('HH:mm'),
      }
      const days = values.days as number[] | undefined
      if (days && days.length > 0) {
        params.days = days
      }
      return params
    }

    case 'team_bye_period': {
      const range = values.bye_date_range as Dayjs[] | undefined
      return {
        start_date: range?.[0]?.format('YYYY-MM-DD'),
        end_date: range?.[1]?.format('YYYY-MM-DD'),
      }
    }

    case 'no_back_to_back':
      return { min_minutes_between: values.min_minutes_between }

    case 'min_rest_days':
      return { min_days: values.min_days }

    case 'bye_distribution':
      return { max_consecutive_byes: values.max_consecutive_byes }

    case 'home_away_balance':
      return { target_home_percentage: values.target_home_percentage || 50 }

    case 'preferred_day':
      return { days: values.preferred_days || [] }

    case 'restricted_day':
      return { days: values.restricted_days || [] }

    case 'max_consecutive_late_games':
      return {
        late_time_threshold: (values.late_time_threshold as Dayjs)?.format(
          'HH:mm'
        ),
        max_consecutive: values.max_consecutive,
      }

    case 'preferred_time':
      return {
        start_time: (values.pref_start_time as Dayjs)?.format('HH:mm'),
        end_time: (values.pref_end_time as Dayjs)?.format('HH:mm'),
      }

    case 'max_games_per_team_per_day':
      return { max_games: values.max_games }

    case 'games_per_team_per_week':
      return { games_per_week: values.games_per_week }

    case 'facility_distribution':
      // `targets` is the ONLY parameter the API accepts.
      return {
        targets: facilityPercentageRowsToTargets(
          values.targets as FacilityPercentageTargetRow[] | undefined
        ),
      }

    default:
      return {}
  }
}

/**
 * Returns default form values for a given constraint type code.
 */
export const getDefaultValues = (typeCode: string): Record<string, unknown> => {
  switch (typeCode) {
    case 'min_rest_days':
      return { min_days: 1 }
    case 'no_back_to_back':
      return { min_minutes_between: 60 }
    case 'max_games_per_team_per_day':
      return { max_games: 2 }
    case 'games_per_team_per_week':
      return { games_per_week: 1 }
    case 'venue_capacity':
      return { max_concurrent_games: 1 }
    case 'bye_distribution':
      return { max_consecutive_byes: 2 }
    case 'home_away_balance':
      return { target_home_percentage: 50 }
    case 'max_consecutive_late_games':
      return { max_consecutive: 2 }
    case 'facility_distribution':
      // One blank target row, so the editor never opens empty.
      return {
        targets: [{ level: 'facility' }] as FacilityPercentageTargetRow[],
      }
    default:
      return {}
  }
}

/**
 * Imperative validation for constraint form values.
 * Returns an error message string, or null if valid.
 */
export const validateConstraintValues = (
  typeCode: string,
  values: Record<string, unknown>
): string | null => {
  switch (typeCode) {
    case 'venue_blackout': {
      const dates = values.dates as unknown[] | undefined
      if (!dates || dates.length === 0) {
        return 'At least one date is required'
      }
      return null
    }
    case 'venue_capacity': {
      const facilityUids = values.facility_uids as string[] | undefined
      if (!facilityUids || facilityUids.length === 0) {
        return 'At least one facility is required'
      }
      if (!values.max_concurrent_games) {
        return 'Max concurrent games is required'
      }
      return null
    }
    case 'no_back_to_back':
      if (!values.min_minutes_between) {
        return 'Minimum time between games is required'
      }
      return null
    case 'min_rest_days':
      if (!values.min_days) {
        return 'Minimum rest days is required'
      }
      return null
    case 'team_bye_period': {
      const range = values.bye_date_range as unknown[] | undefined
      if (!range || range.length < 2) {
        return 'Date range is required'
      }
      return null
    }
    case 'preferred_day': {
      const days = values.preferred_days as number[] | undefined
      if (!days || days.length === 0) {
        return 'At least one day is required'
      }
      return null
    }
    case 'restricted_day': {
      const rDays = values.restricted_days as number[] | undefined
      if (!rDays || rDays.length === 0) {
        return 'At least one day is required'
      }
      return null
    }
    case 'max_consecutive_late_games': {
      if (!values.late_time_threshold) {
        return 'Late game threshold time is required'
      }
      if (!values.max_consecutive) {
        return 'Max consecutive late games is required'
      }
      return null
    }
    case 'max_games_per_team_per_day':
      if (!values.max_games) {
        return 'Max games per day is required'
      }
      return null
    case 'games_per_team_per_week':
      if (!values.games_per_week) {
        return 'Games per week is required'
      }
      return null
    case 'facility_distribution':
      return validateFacilityPercentageRows(
        values.targets as FacilityPercentageTargetRow[] | undefined
      )
    default:
      return null
  }
}
